import { Specimen, SpecimenProtocolOverride } from "pwapi";

import { LabSettings } from "pwapi/custom/LabSettings";

import { GrossingSelections } from "./selections";

// GrossingConditions encapsulate logical if statements which take GrossingSelections as input and return a boolean.
// GrossingConditions can be used to show, hide, or change the output of GrossingTokens, GrossingModifiers, GrossingConditionals, and GrossingSegments.

export type GrossingCondition = (
  selections: GrossingSelections,
  settings?: LabSettings
) => boolean;

export const isSkin: GrossingCondition = (s) =>
  s.specimen.type === Specimen.type.SKIN;
export const isNail: GrossingCondition = (s) =>
  !!s.specimen.protocols?.includes(SpecimenProtocolOverride.protocol.NAIL);
export const isSlowMohs: GrossingCondition = (s) =>
  !!s.specimen.protocols?.includes(SpecimenProtocolOverride.protocol.SLOW_MOHS);
export const isSlowMohsWhole: GrossingCondition = (s) =>
  isSlowMohs(s) &&
  ["annular", "circular", "o'shaped"].includes(
    s.tokenSelections.slow_mohs_shape?.value || ""
  );
export const isSlowMohsDebulk: GrossingCondition = (s) =>
  isSlowMohs(s) && s.tokenSelections.slow_mohs_shape?.value === "debulk";
export const isSlowMohsMargin: GrossingCondition = (s) =>
  isSlowMohs(s) &&
  s.tokenSelections.slow_mohs_shape?.value === "circumferential margin";
export const isExcision: GrossingCondition = (s) =>
  !isNail(s) && s.tokenSelections.type?.value === "excision";
export const isPunch: GrossingCondition = (s) =>
  !isNail(s) && s.tokenSelections.type?.value === "punch";
export const isShave: GrossingCondition = (s) =>
  !isNail(s) && s.tokenSelections.type?.value === "shave";
export const isDIF: GrossingCondition = (s) => isShave(s) || isPunch(s);
export const isDIFSelected: GrossingCondition = (s) =>
  s.modifierSelections.dif && !isExcision(s);
export const isOrientedEllipse: GrossingCondition = (s) =>
  isExcision(s) && s.modifierSelections.oriented;
export const isAdditionalOrientingMark: GrossingCondition = (s) =>
  isExcision(s) && s.modifierSelections["additional-orienting-mark"];
export const isHalves: GrossingCondition = (s) =>
  isOrientedEllipse(s) && s.modifierSelections.halves;
export const isEllipse: GrossingCondition = (s) =>
  isExcision(s) && s.modifierSelections.ellipse;
export const isEnFace: GrossingCondition = (s) =>
  isOrientedEllipse(s) && s.modifierSelections["en-face"];
export const hasOrientedSubmission: GrossingCondition = (s) =>
  isOrientedEllipse(s) && s.modifierSelections["oriented-submission"];
export const isExcisionWithTips: GrossingCondition = (s) =>
  isExcision(s) && s.modifierSelections.tips;
export const isMultipleExcisions: GrossingCondition = (s) =>
  isExcision(s) && s.modifierSelections.multipleExcisions;
export const isMultiplePunches: GrossingCondition = (s) =>
  isPunch(s) && s.modifierSelections.multiplePunches;
export const isMultiplePunchesInSingleCassette: GrossingCondition = (s) =>
  isPunch(s) &&
  s.modifierSelections.multiplePunches &&
  s.modifierSelections.singleCassette;
export const isMultipleShaves: GrossingCondition = (s) =>
  isShave(s) && s.modifierSelections.multipleShaves;
export const isMultipleShavesInSingleCassette: GrossingCondition = (s) =>
  isShave(s) &&
  s.modifierSelections.multipleShaves &&
  s.modifierSelections.singleCassette;
export const isShaveFragments: GrossingCondition = (s) =>
  isShave(s) && s.modifierSelections.shaveFragments;
export const isSoftTissue: GrossingCondition = (s) =>
  s.specimen.type === "SOFT TISSUE";
export const isSoftTissueWithSkin: GrossingCondition = (s) =>
  isSoftTissue(s) && s.modifierSelections.skinAttached;
export const isLipoma: GrossingCondition = (s) =>
  (isSoftTissue(s) || isSkin(s)) && s.modifierSelections.lipoma;
export const isEmpty: GrossingCondition = (s) => s.modifierSelections.empty;
export const isEyeWedge: GrossingCondition = (s) =>
  isExcision(s) && isSkin(s) && s.modifierSelections.eyeWedge;
export const isFalseMargins: GrossingCondition = (s) =>
  !!s.modifierSelections.falseMargins;
export const isNailAvulsion: GrossingCondition = (s) =>
  !!s.specimen.procedure && s.specimen.procedure === Specimen.procedure.NA;
export const isForeignBodyProcedure: GrossingCondition = (s) =>
  !!s.specimen.procedure && s.specimen.procedure === Specimen.procedure.FB;
export const isForeignBodyModifier: GrossingCondition = (s) =>
  s.modifierSelections.foreignBody;
export const isTissueModifier: GrossingCondition = (s) =>
  s.modifierSelections.foreignBody && s.modifierSelections.tissue;
export const isWetMountModifier: GrossingCondition = (s) =>
  s.modifierSelections.foreignBody && s.modifierSelections.wetMount;
export const isSendoutModifier: GrossingCondition = (s) =>
  s.modifierSelections.foreignBody && s.modifierSelections.sendout;
export const isForeignBodyEligible: GrossingCondition = (s) =>
  (s.specimen.type === Specimen.type.SKIN ||
    s.specimen.type === Specimen.type.OTHER) &&
  (s.specimen.procedure === Specimen.procedure.FB ||
    s.specimen.procedure === Specimen.procedure.O);
